import { useEffect, useState } from 'react';

import useIsBrowser from 'hooks/useIsBrowser';
// TODO: Replace this with a better way to import without default
// eslint-disable-next-line import/no-named-default
import { default as isOnPDP } from 'commons/isPDP';

// React Suspense and Next.js segment-cache transitions can briefly hide the
// PDP element via `display: none` while a route segment is streaming in.
// Wait this long before treating an invisible PDP element as "no longer on a
// PDP" so the header doesn't flicker during those transient hides. Long
// enough to absorb hydration / streaming pauses, short enough to feel
// instant on real navigation.
const HIDE_GRACE_MS = 750;

const useIsPDP = () => {
  // `useIsBrowser` flips to `true` only after the first post-hydration
  // commit. Gating the observer setup on it ensures we don't trigger
  // setState here during React's hydration phase, which would otherwise
  // interrupt selective-hydration recovery in unrelated Suspense boundaries
  // and surface as hydration mismatch errors.
  const isBrowser = useIsBrowser();
  const [isPDP, setIsPDP] = useState(false);

  useEffect(() => {
    if (!isBrowser) return undefined;

    let pendingHideTimer;

    const cancelPendingHide = () => {
      if (pendingHideTimer) {
        clearTimeout(pendingHideTimer);
        pendingHideTimer = undefined;
      }
    };

    const checkIfPDP = () => {
      if (isOnPDP()) {
        cancelPendingHide();
        setIsPDP(true);
      } else if (!pendingHideTimer) {
        pendingHideTimer = setTimeout(() => {
          pendingHideTimer = undefined;
          if (!isOnPDP()) setIsPDP(false);
        }, HIDE_GRACE_MS);
      }
    };

    checkIfPDP();

    // The PDP marker elements may be added, removed, or hidden/shown after
    // mount — both because of progressive rendering during the initial
    // page load and because Next.js route-segment caching can hide the
    // previous segment with `display: none` after a client-side
    // navigation. Observe both child mutations and `style` attribute
    // changes so we react to either case.
    const observer = new MutationObserver(checkIfPDP);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      cancelPendingHide();
      observer.disconnect();
    };
  }, [isBrowser]);

  return isPDP;
};

export default useIsPDP;
